import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu, Moon, Sun, LogOut, User, Wallet } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { useCash } from '../../context/CashContext';
import { CloseDayDialog } from '../cash/CloseDayDialog';
import { RemoteOrdersBell } from './RemoteOrdersBell';
import { FunctionsLauncher } from './FunctionsLauncher';
import { PageTitle } from './PageTitle';
import { Button } from '../ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../ui/dropdown-menu';

export const Topbar = ({ onToggleSidebar, onToggleMobile }) => {
  const { t } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const { user, logout } = useAuth();
  const { session } = useCash();
  const [closeOpen, setCloseOpen] = useState(false);
  const isManager = ['admin', 'manager'].includes(user?.role);

  return (
    <header className="relative h-14 shrink-0 border-b border-border bg-card flex items-center gap-2 px-4">
      <button onClick={onToggleSidebar} className="hidden md:flex h-9 w-9 items-center justify-center rounded-lg hover:bg-muted text-muted-foreground">
        <Menu className="h-5 w-5" />
      </button>
      <button onClick={onToggleMobile} className="md:hidden h-9 w-9 flex items-center justify-center rounded-lg hover:bg-muted text-muted-foreground">
        <Menu className="h-5 w-5" />
      </button>

      <div className="ml-1 min-w-0 flex-1">
        <PageTitle />
      </div>

      {/* Pulsante Funzioni centrato orizzontalmente sullo schermo. */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
        <FunctionsLauncher />
      </div>

      <div className="ml-auto flex items-center gap-2">
        <RemoteOrdersBell />
        <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle" aria-label={t('topbar.changeTheme')}>
          {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-2 h-10 pl-2 pr-3 rounded-lg hover:bg-muted" data-testid="user-menu">
              <div className="h-8 w-8 rounded-full bg-primary/15 text-primary flex items-center justify-center">
                <User className="h-4 w-4" />
              </div>
              <div className="hidden sm:block text-left">
                <div className="text-sm font-medium leading-tight">{user?.name}</div>
                <div className="text-xs text-muted-foreground leading-tight">{t(`roles.${user?.role}`)}</div>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuLabel>{user?.name}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {isManager && session && (
              <>
                <DropdownMenuItem onClick={() => setCloseOpen(true)} data-testid="close-day-menu-item">
                  <Wallet className="h-4 w-4 mr-2" /> {t('topbar.closeDay')}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuItem onClick={logout} data-testid="logout-button" className="text-destructive focus:text-destructive">
              <LogOut className="h-4 w-4 mr-2" /> {t('topbar.logout')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <CloseDayDialog open={closeOpen} onOpenChange={setCloseOpen} />
    </header>
  );
};
